<template>
    <div class="hero-img" :class="[sizeOfHero === 'small' ? 'small' : 'big', { 'has-slide-box': hasBoxes }]">
        <custom-parallax
            v-if="images && images[0] && herotype === 'parallax'"
            :image="images[0].image"
            :title="images[0].title"
            :alt="images[0].alt"
            :copyright="images[0].copyright"
            style="width: 100%"
        />

        <div v-else-if="herotype === 'slider' && images && images[0]" class="hero-slider">
            <v-carousel v-model="activeSlide" cycle hide-delimiters>
                <v-carousel-item v-for="(image, k) in images" :key="k">
                    <custom-image
                        :image="image"
                        width="100%"
                        height="100%"
                        :hover="false"
                        cover
                        min-thumbnail="1280"
                    />
                </v-carousel-item>
            </v-carousel>
            <div
                v-if="hasBoxes"
                class="hero-slide-box-layer"
                :class="'box-' + (images[activeSlide]?.boxPosition || 'left')"
            >
                <v-container>
                    <div class="hero-slide-box-stack">
                        <template v-for="(image, k) in images" :key="k">
                            <hero-slide-box
                                v-if="image.box"
                                :box="image.box"
                                :class="{ 'is-active': k === activeSlide }"
                            />
                        </template>
                    </div>
                </v-container>
            </div>
        </div>

        <custom-image
            v-else-if="images && images[0]"
            :image="images[0]"
            width="100%"
            height="100%"
            :hover="false"
            :show-original="true"
            cover
        />
    </div>
</template>

<script setup>
import CustomImage from "@/components/reuseables/customImage.vue";
import CustomParallax from "@/components/reuseables/customParallax.vue";
import HeroSlideBox from "@/components/object_header/heroSlideBox.vue";
import { computed, defineProps, ref } from "vue";

const props = defineProps({
    images: Array,
    sizeOfHero: {
        type: String,
        default: "small",
        validator(value) {
            return ["small", "big"].includes(value);
        },
    },
    herotype: {
        type: String,
    },
});

const activeSlide = ref(0);
const hasBoxes = computed(() => Boolean(props.images?.some((image) => image.box)));
</script>

<style scoped>
.hero-slider {
    position: relative;
    height: 100%;
}

.has-slide-box .hero-slider {
    display: flex;
    flex-direction: column;
    height: auto;
    min-height: inherit;
}

.has-slide-box .hero-slider :deep(.v-carousel) {
    position: absolute;
    inset: 0;
}

.has-slide-box .hero-slider :deep(.v-window__controls) {
    z-index: 4;
}

.hero-slide-box-layer {
    position: relative;
    padding-top: calc(var(--app-bar-height, 0px) + 1.5rem);
    padding-bottom: calc(var(--hero-title-height, 0px) + 1.5rem);
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    pointer-events: none;
    z-index: 3;
}

.hero-slide-box-layer :deep(.v-container) {
    position: static;
    display: flex;
    align-items: center;
    min-height: 0;
    padding-block: 0 !important;
    pointer-events: none;
}

.hero-slide-box-stack {
    display: grid;
    min-width: 0;
}

.hero-slide-box-stack > :deep(*) {
    grid-area: 1 / 1;
    align-self: center;
    visibility: hidden;
}

.hero-slide-box-stack > :deep(.is-active) {
    visibility: visible;
}

.hero-slide-box-layer.box-left :deep(.v-container) {
    justify-content: flex-start;
}

.hero-slide-box-layer.box-right :deep(.v-container) {
    justify-content: flex-end;
}

@media only screen and (max-width: 64em) {
    .has-slide-box .hero-slider :deep(.v-window__controls) {
        padding-inline: 0;
    }

    .has-slide-box .hero-slider :deep(.v-window__controls .v-btn) {
        width: 1.5rem;
        height: 1.5rem;
        min-width: 0;
    }

    .has-slide-box .hero-slider :deep(.v-window__controls .v-icon) {
        font-size: 1.25rem;
    }
}

@media only screen and (max-width: 37.5em) {
    .hero-slide-box-layer {
        align-items: flex-end;
    }

    .hero-slide-box-layer :deep(.v-container) {
        align-items: flex-end;
    }

    .hero-slide-box-layer.box-left :deep(.v-container),
    .hero-slide-box-layer.box-right :deep(.v-container) {
        justify-content: center;
    }
}
</style>

<style lang="scss" scoped>
.hero-img {
    width: 100%;
    height: 50vh;
    max-height: 50vh;

    &.has-slide-box {
        height: auto !important;
        max-height: none;
        min-height: min(32.5rem, 50vh);

        @media (max-width: 959px) {
            min-height: 17rem;
        }
    }

    &.small.has-slide-box {
        min-height: min(20rem, 50vh);

        @media (max-width: 959px) {
            min-height: 17rem;
        }
    }

    &.small {
        height: 20rem;
        @media (max-width: 959px) {
            height: 17rem;
        }
    }

    &.big {
        height: 32.5rem;
        @media (max-width: 959px) {
            height: 17rem;
        }
    }
    @media (min-width: 1025px) {
        max-width: calc(100% - 8rem);
        margin: 0 auto;
    }
}
</style>
